import { useState, useRef, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ComboboxInputProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  onBlur?: () => void;
  disabled?: boolean;
}

export function ComboboxInput({
  value,
  onChange,
  suggestions,
  placeholder = "Select or type...",
  onBlur,
  disabled = false,
}: ComboboxInputProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? "");
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const filtered = suggestions.filter((s) =>
    s.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleSelect = (selected: string) => {
    onChange(selected);
    setInputValue(selected);
    setOpen(false);
  };

  const handleInputChange = (val: string) => {
    setInputValue(val);
    onChange(val);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && onBlur) {
      onBlur();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-9 px-3 text-sm"
          type="button"
          disabled={disabled}
        >
          <span className={cn("truncate", !inputValue && "text-muted-foreground")}>
            {inputValue || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: triggerRef.current?.offsetWidth }}
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={inputValue}
            onValueChange={handleInputChange}
          />
          <CommandList>
            {filtered.length === 0 && inputValue ? (
              <CommandEmpty>
                <span className="text-sm text-muted-foreground">
                  Press Enter or click elsewhere to use &ldquo;{inputValue}&rdquo;
                </span>
              </CommandEmpty>
            ) : filtered.length === 0 ? (
              <CommandEmpty>No categories yet.</CommandEmpty>
            ) : (
              <CommandGroup>
                {filtered.map((s) => (
                  <CommandItem
                    key={s}
                    value={s}
                    onSelect={() => handleSelect(s)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === s ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {s}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
